// details/summaryを基本とし、div構成ではボタン相当の属性とキー操作を補完する。
//
// ============================================
// HTML 構造例:
// ============================================
// <details class="js-accordion">
//   <summary class="js-accordion__ttlWrap">...</summary>
//   <div class="js-accordion__contents">...</div>
// </details>
// ============================================
// CSS 注意:
// is-active / is-open 等の表示・トランジションは CSS 側で定義する。

import { gsap } from 'gsap';
import { bindOnce } from '../utils/bindOnce';

// ここを変えるだけでアニメーションの見た目を調整できる
const CONFIG = {
  triggerSelector: '.js-accordion__ttlWrap', // 開閉トリガー
  accordionSelector: '.js-accordion',        // アコーディオン本体
  contentSelector: '.js-accordion__contents', // 開閉するコンテンツ領域
  contentMarginTop: 40,                      // 開いたとき content に付く marginTop（px）
  closeDuration: 0.8,                        // 閉じるアニメーションの時間（秒）
  openDuration: 0.8,                         // 開くアニメーションの時間（秒）
  closeEase: 'power1.inOut',                 // 閉じるアニメーションのイージング
  openEase: 'power2.inOut',                  // 開くアニメーションのイージング
  openAttr: 'open',                          // 開いた状態を示す属性（<details> の open に相当）
  runningValue: 'running',                   // アニメーション中を示す data 属性値
};

// ページ内の全アコーディオンを初期化する
function accordionFunc() {
  const triggers = document.querySelectorAll(CONFIG.triggerSelector);
  if (triggers.length === 0) return;

  triggers.forEach((trigger) => {
    if (!bindOnce(trigger, 'accordion')) return;

    const accordion = trigger.closest(CONFIG.accordionSelector);
    const content   = accordion?.querySelector(CONFIG.contentSelector);

    if (!accordion || !content) return;

    // summary / button はネイティブで対応済みのため、div 等の場合だけ属性を補完する
    const isNativeInteractive = trigger.tagName === 'SUMMARY' || trigger.tagName === 'BUTTON';

    if (!isNativeInteractive) {
      if (!trigger.hasAttribute('role')) {
        trigger.setAttribute('role', 'button');
      }
      if (!trigger.hasAttribute('tabindex')) {
        trigger.setAttribute('tabindex', '0');
      }
    }

    // summary は親 details の open 属性で状態が伝わるため、aria-expanded は付けない
    // （二重に付けると支援技術の読み上げが重複・矛盾する）
    const usesAriaExpanded = trigger.tagName !== 'SUMMARY';
    const syncExpanded = (isOpen) => {
      if (usesAriaExpanded) {
        trigger.setAttribute('aria-expanded', isOpen ? 'true' : 'false');
      }
    };
    syncExpanded(accordion.hasAttribute(CONFIG.openAttr));

    const toggle = () => {
      // アニメーション中はスキップ（クリック連打防止）
      if (accordion.dataset.animStatus === CONFIG.runningValue) return;

      if (accordion.hasAttribute(CONFIG.openAttr)) {
        syncExpanded(false);
        closeAccordion(accordion, content);
      } else {
        syncExpanded(true);
        openAccordion(accordion, content);
      }
    };

    trigger.addEventListener('click', (e) => {
      // summary の標準即時開閉（および <a> の遷移）を止めて GSAP に置き換える
      e.preventDefault();
      toggle();
    });

    // summary / button はキー押下でネイティブに click が発火し二重発火になるため、
    // Enter / Space の登録は div 構成の場合のみ行う
    if (!isNativeInteractive) {
      trigger.addEventListener('keydown', (e) => {
        if (e.key !== 'Enter' && e.key !== ' ') return;
        e.preventDefault();
        toggle();
      });
    }
  });
}

// アコーディオンを閉じるアニメーション
function closeAccordion(accordion, content) {
  accordion.dataset.animStatus = CONFIG.runningValue;

  gsap.fromTo(
    content,
    {
      height: content.offsetHeight,
      opacity: 1,
      marginTop: CONFIG.contentMarginTop,
    },
    {
      height: 0,
      opacity: 0,
      marginTop: 0,
      duration: CONFIG.closeDuration,
      ease: CONFIG.closeEase,
      onComplete: () => {
        accordion.removeAttribute(CONFIG.openAttr);
        gsap.set(content, { clearProps: 'height' }); // GSAP のインラインスタイルを掃除
        accordion.dataset.animStatus = '';
      },
    }
  );
}

// アコーディオンを開くアニメーション
function openAccordion(accordion, content) {
  accordion.setAttribute(CONFIG.openAttr, ''); // 先に open を付けて DOM に表示させる
  accordion.dataset.animStatus = CONFIG.runningValue;

  // open 直後は height が auto のため、1 フレーム待って scrollHeight を正確に取得する
  requestAnimationFrame(() => {
    const targetH = content.scrollHeight;

    gsap.fromTo(
      content,
      {
        height: 0,
        opacity: 0,
        marginTop: 0,
      },
      {
        height: targetH,
        opacity: 1,
        marginTop: CONFIG.contentMarginTop,
        duration: CONFIG.openDuration,
        ease: CONFIG.openEase,
        onComplete: () => {
          // コンテンツが動的に変化しても追従できるよう height を auto に戻す
          gsap.set(content, { height: 'auto' });
          accordion.dataset.animStatus = '';
        },
      }
    );
  });
}

export { accordionFunc };
